import { sumBy } from 'lodash';
import { getAccessoryPrice, isAccessoryApplicable } from '../accessory-utils';
import { AccessoryConfig, SelectedAccessory } from '../legos/accessoryTypes';
import { Car } from '../legos/carTypes';
import { Deal } from '../legos/dealTypes';
import { FinanceResponse } from '../legos/financingTypes';
import { Bundle, SelectedProductWithFinalPrice } from '../legos/penTypes';
import { AddOn } from '../legos/pencilTypes';
import { calculateFinalPrice } from '../pen-utils';

export function getSelectedProductsWithFinalPrice({
  financeResponse,
  deal,
}: {
  financeResponse: FinanceResponse;
  deal?: Deal | null;
}): {
  selectedBundles: Bundle[];
  selectedProducts: SelectedProductWithFinalPrice[];
} {
  const selectedBundles = financeResponse.data?.selectedBundles ?? [];
  const priceOverrides =
    financeResponse.data?.protectionPlanProductPriceOverrides ?? {};
  const selectedProducts = (financeResponse.data?.selectedProducts ?? []).map(
    product => ({
      ...product,
      finalPrice: calculateFinalPrice(
        product,
        selectedBundles,
        priceOverrides,
        deal,
      ),
    }),
  );
  return { selectedBundles, selectedProducts };
}

export type ProductTotalsResult = {
  selectedAccessories: SelectedAccessory[];
  selectedAddOns: AddOn[];
  selectedBundles: Bundle[];
  selectedProducts: SelectedProductWithFinalPrice[];
  totalAccessoryPrice: number;
  totalAddOnPrice: number;
  totalProductPrice: number;
  totalAdditionalProductsAmount: number;
  totalAdditionalProductsAmountWithTaxes: number;
  totalAdditionalProductsCount: number;
};
type ProductTotalsInput = {
  financeResponse: FinanceResponse;
  accessoryConfig?: AccessoryConfig | null;
  car?: Car | null;
  deal?: Deal | null;
};

/**
 * Calculates the total price of all selected products, accessories, and
 * add-ons for a finance response.
 */
export function calculateProductTotals({
  financeResponse,
  accessoryConfig,
  car,
  deal,
}: ProductTotalsInput): ProductTotalsResult {
  const selectedAccessories = financeResponse.data?.selectedAccessories ?? [];
  const selectedAddOns = financeResponse.data?.selectedAddOns ?? [];
  const {
    selectedBundles,
    selectedProducts,
  } = getSelectedProductsWithFinalPrice({
    deal,
    financeResponse,
  });

  const totalAddOnPrice = sumBy(selectedAddOns, 'price');

  // Get applicable accessories
  const carAccessories = (accessoryConfig?.accessories ?? [])
    .filter(a => isAccessoryApplicable(a, car))
    .concat(car?.optionalAccessories ?? []);

  // Calculate total price
  const totalAccessoryPrice = carAccessories
    .filter(a => selectedAccessories.some(s => s.id === a.id))
    .reduce((total, a) => total + getAccessoryPrice(a), 0);

  const totalProductPrice = selectedProducts.reduce(
    (total, product) => total + product.finalPrice,
    0,
  );

  const taxPercent = financeResponse.data?.taxPercent ?? 0;
  const totalProductTaxAmount = selectedProducts.reduce(
    (total, product) =>
      total + (product.taxed ? product.finalPrice * taxPercent : 0),
    0,
  );
  const totalAccessoryTaxAmount = selectedAccessories.reduce(
    (total, accessory) =>
      total + (accessory.taxed ? getAccessoryPrice(accessory) * taxPercent : 0),
    0,
  );

  const totalSelectedAddOnTaxAmount = selectedAddOns.reduce(
    (total, addOn) =>
      total + (addOn.taxed && addOn.price ? addOn.price * taxPercent : 0),
    0,
  );

  const totalAdditionalProductsAmount =
    totalProductPrice + totalAccessoryPrice + totalAddOnPrice;
  const totalAdditionalProductsAmountWithTaxes =
    totalAdditionalProductsAmount +
    totalProductTaxAmount +
    totalAccessoryTaxAmount +
    totalSelectedAddOnTaxAmount;
  const totalAdditionalProductsCount =
    selectedAccessories.length +
    selectedAddOns.length +
    selectedProducts.length;

  return {
    selectedAccessories,
    selectedAddOns,
    selectedBundles,
    selectedProducts,
    totalAccessoryPrice,
    totalAddOnPrice,
    totalProductPrice,
    totalAdditionalProductsAmount,
    totalAdditionalProductsAmountWithTaxes,
    totalAdditionalProductsCount,
  };
}
